import { z } from 'zod'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import { optionalProjectId } from '../../common/optionalFields'
import { ProcessingDetails } from './connector'

export const RagFileUploadState = {
  Unknown: 'unknown',
  Uploading: 'uploading',
  Processing: 'processing',
  Complete: 'complete',
  Error: 'error'
} as const

export type RagFileUploadState = (typeof RagFileUploadState)[keyof typeof RagFileUploadState]

export const RagFileSchema = z
  .object({
    fileId: z.number(),
    fileName: z.string(),
    description: z.string(),
    fileUploadState: z.enum(RagFileUploadState),
    processingDetails: z.enum(ProcessingDetails),
    createdOnTsMs: z.number(),
    createdById: z.number(),
    createdByFirstName: z.string(),
    createdBySurname: z.string(),
    updatedAtTsMs: z.number(),
    updatedById: z.number(),
    updatedByFirstName: z.string(),
    updatedBySurname: z.string()
  })
  .describe('RagFileSchema')

export type RagFile = z.infer<typeof RagFileSchema>

export const GetRagFileResponseSchema = z
  .object({
    items: z.array(RagFileSchema),
    pageCount: z.number(),
    documentCount: z.number().optional()
  })
  .describe('GetRagFileResponseSchema')

export type GetRagFileResponse = z.infer<typeof GetRagFileResponseSchema>

export const AddRagFileSchema = z.object({
  fileName: z.string().min(1, 'File is required')
})

export type AddRagFile = z.infer<typeof AddRagFileSchema>

// Mirrors SearchRequestV2 in backend/internal/api/http/rag_content_management.go
export const FetchRagFilesSchema = z.object({
  docId: z.number().optional(),
  searchText: z.string().optional(),
  tagIds: z.array(z.number()).optional(),
  userIds: z.array(z.number()).optional(),
  currentPage: z.number(),
  projectId: z.number().optional()
})

export type FetchRagFiles = z.infer<typeof FetchRagFilesSchema>

const defaultFetchParams: FetchRagFiles = {
  currentPage: 1
}

export async function fetchRagFiles(params: Partial<FetchRagFiles> = {}): Promise<GetRagFileResponse> {
  const body: FetchRagFiles = { ...defaultFetchParams, ...params }
  const res = await fetch('/api/content/file/searchv2', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body)
  })
  await apiErrorHandler(res)
  const data = await res.json()
  return parseDataOfType(data, GetRagFileResponseSchema)
}

const AddFileResponseSchema = z.object({
  fileID: z.number(),
  preSignedUrl: z.string()
})

export async function addRagFile(file: File, description: string, projectId?: number): Promise<void> {
  const registerRes = await fetch('/api/content/file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      fileName: file.name,
      description,
      tagIds: [],
      ...optionalProjectId(projectId)
    })
  })
  await apiErrorHandler(registerRes)
  const registerData = await registerRes.json()
  const { preSignedUrl } = parseDataOfType(registerData, AddFileResponseSchema)

  const uploadRes = await fetch(preSignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  })
  if (!uploadRes.ok) {
    throw new Error(`File upload failed: ${uploadRes.statusText}`)
  }
}

export async function updateRagFile(fileId: number, description: string, projectId?: number): Promise<void> {
  const res = await fetch(`/api/content/file/${fileId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ description, tagIds: [], ...optionalProjectId(projectId) })
  })
  await apiErrorHandler(res)
}

export async function deleteRagFile(fileId: number, projectId?: number): Promise<void> {
  const url =
    projectId !== undefined ? `/api/content/file/${fileId}?projectId=${projectId}` : `/api/content/file/${fileId}`
  const res = await fetch(url, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function getRagFileDownloadUrl(fileId: number): Promise<string> {
  const res = await fetch(`/api/content/file/${fileId}/download`, {
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data = await res.json()
  const parsed = parseDataOfType(data, z.object({ downloadUrl: z.string() }))
  return parsed.downloadUrl
}
